import { useEffect, useState } from 'react';
import { useTools } from '../../../../ToolProvider.jsx';
import InputSpinner from '../InputSpinner2/InputSpinner2.jsx';

const round1 = (value) => Math.round(value * 10) / 10;

/**
 * Redondeo de las esquinas de los rectángulos seleccionados.
 *
 * El lienzo es la única fuente de verdad: el radio se relee de la selección en
 * vez de guardarse aparte, así que escalar el rectángulo, deshacer o cambiar de
 * selección dejan el panel al día sin sincronizar nada a mano.
 */
const ShapePanel = () => {
    const { canvas, getRectRadius, onChangeRectRadius } = useTools();
    const [selection, setSelection] = useState(null);

    useEffect(() => {
        if (!canvas) return undefined;
        const refresh = () => setSelection(getRectRadius?.() ?? null);
        refresh();
        const events = [
            'selection:created', 'selection:updated', 'selection:cleared',
            'object:modified', 'object:scaling', 'object:removed',
            'layer:nested-selection', 'layer:changed',
        ];
        events.forEach(event => canvas.on(event, refresh));
        return () => events.forEach(event => canvas.off(event, refresh));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canvas]);

    if (!selection) {
        return (
            <div className="rounded-lg border border-base-300 bg-base-200 p-3 text-sm leading-snug">
                Selecciona un rectángulo en el lienzo para redondear sus esquinas.
                <span className="mt-2 block text-xs opacity-60">
                    El redondeo del fondo se ajusta desde el panel de Base/Cuerpo.
                </span>
            </div>
        );
    }

    const { count, radiusMm, maxRadiusMm, mixed } = selection;
    const max = round1(maxRadiusMm);
    const value = Math.min(round1(radiusMm), max);
    const apply = (nextMm) => onChangeRectRadius?.(
        Math.min(Math.max(round1(nextMm), 0), max),
    );

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between gap-2">
                <span className="text-sm">
                    {count === 1
                        ? 'Rectángulo seleccionado'
                        : `${count} rectángulos seleccionados`}
                </span>
                {mixed && (
                    <span className="badge badge-warning badge-sm">Radios distintos</span>
                )}
            </div>

            <div>
                <label htmlFor="rect_radius" className="mb-1 block text-sm font-medium">
                    Radio de las esquinas (mm)
                </label>
                <input
                    id="rect_radius"
                    type="range"
                    className="range range-primary range-sm"
                    min={0}
                    max={max}
                    step={0.1}
                    value={value}
                    onChange={(event) => apply(Number(event.target.value))}
                />
                <div className="mt-2">
                    <InputSpinner
                        value={value}
                        onChange={apply}
                        min={0}
                        max={max}
                        initialStep={0.25}
                    />
                </div>
            </div>

            <div className="flex gap-2">
                <button type="button" className="btn btn-xs flex-1" onClick={() => apply(0)}>
                    Sin redondeo
                </button>
                <button type="button" className="btn btn-xs flex-1" onClick={() => apply(max)}>
                    Máximo
                </button>
            </div>

            <p className="text-xs leading-snug opacity-60">
                El máximo ({max} mm) es la mitad del lado más corto: a partir de ahí
                el rectángulo ya es una cápsula. Si el rectángulo está estirado, el
                radio se corrige por eje para que la esquina salga redonda y no ovalada.
            </p>
        </div>
    );
};

export default ShapePanel;
